import React from "react";
import { Link } from "react-router-dom";

/**
 * Renders the FAQ answer Markdown subset (contract v1.2 §3.13.3) as React
 * elements, never as HTML:
 *
 * - paragraphs separated by a blank line (a single line break stays a <br>)
 * - `**bold**` and `*italic*`
 * - `- ` bullet lists (consecutive lines starting with "- ")
 * - `[text](target)` links, only when linkKind(target) allows the target
 *
 * Anything else, including raw HTML and a link with any other target, is
 * shown as literal text. The backend refuses other link syntax on write; the
 * allowlist here is checked again whatever the API sends.
 *
 * `{{token}}` placeholders are filled from `tokens` before parsing (see
 * fillTokens), so a token may sit inside a link target.
 *
 * @param {object} props
 * @param {string} props.source - The Markdown text
 * @param {object} [props.tokens] - Token values, see fillTokens
 */
export default function MarkdownText({ source, tokens }) {
  const text = tokens ? fillTokens(source, tokens) : String(source || "");
  return <>{renderBlocks(text)}</>;
}

/* ------------------------------------------------------------------ */
/* Link allowlist                                                      */
/* ------------------------------------------------------------------ */

// Whitespace, C0/C1 controls and backslashes: URL parsers strip or reinterpret
// them (e.g. "/\evil.example" is protocol-relative), so a target holding one
// is never a link.
// eslint-disable-next-line no-control-regex
const UNSAFE_TARGET_CHARS = /[\u0000-\u0020\u007f-\u009f\\]/;

/**
 * How a link target may be rendered, or null when it must stay text.
 * - "internal": root-relative path ("/policies#refund"), not "//host"
 * - "external": absolute https:// URL
 * - "mailto": mailto: address
 *
 * @param {unknown} target
 * @returns {'internal'|'external'|'mailto'|null}
 */
export function linkKind(target) {
  if (typeof target !== "string" || !target || UNSAFE_TARGET_CHARS.test(target)) return null;
  if (target[0] === "/") return target[1] === "/" ? null : "internal";
  let url;
  try {
    url = new URL(target);
  } catch (error) {
    return null;
  }
  if (/^https:\/\//i.test(target) && url.protocol === "https:" && url.hostname) return "external";
  if (/^mailto:./i.test(target) && url.protocol === "mailto:") return "mailto";
  return null;
}

/* ------------------------------------------------------------------ */
/* Tokens                                                              */
/* ------------------------------------------------------------------ */

const TOKEN = /\{\{([a-z_]+)(?::([a-z0-9-]+))?\}\}/g;

/** Shown for a price token whose service is not in the catalog. */
export const UNKNOWN_PRICE_TEXT = "see Services";

/**
 * Replace `{{name}}` and `{{price:<slug>}}` tokens. `tokens.price(slug)`
 * returns the formatted fee or null (shown as UNKNOWN_PRICE_TEXT); other
 * names map to strings. An unknown token stays as typed.
 *
 * @param {string} text
 * @param {object} tokens
 * @returns {string}
 */
export function fillTokens(text, tokens) {
  return String(text || "").replace(TOKEN, (match, name, arg) => {
    if (name === "price") {
      if (arg === undefined || typeof tokens.price !== "function") return match;
      return tokens.price(arg) || UNKNOWN_PRICE_TEXT;
    }
    if (arg !== undefined) return match;
    const value = tokens[name];
    return typeof value === "string" || typeof value === "number" ? String(value) : match;
  });
}

/* ------------------------------------------------------------------ */
/* Blocks                                                              */
/* ------------------------------------------------------------------ */

const BULLET = /^- /;

function renderBlocks(text) {
  const out = [];
  text
    .replace(/\r\n?/g, "\n")
    .split(/\n[ \t]*\n/)
    .forEach((block) => {
      if (!block.trim()) return;
      // Consecutive "- " lines form a list; other runs of lines a paragraph.
      let run = null;
      block.split("\n").forEach((line) => {
        const isItem = BULLET.test(line);
        if (!run || run.isItem !== isItem) {
          run = { isItem, lines: [] };
          out.push(run);
        }
        run.lines.push(isItem ? line.slice(2) : line);
      });
    });

  return out.map((run, i) =>
    run.isItem ? (
      <ul key={i}>
        {run.lines.map((line, j) => (
          <li key={j}>{renderInline(line, true)}</li>
        ))}
      </ul>
    ) : (
      <p key={i}>
        {run.lines.map((line, j) => (
          <React.Fragment key={j}>
            {j > 0 && <br />}
            {renderInline(line, true)}
          </React.Fragment>
        ))}
      </p>
    )
  );
}

/* ------------------------------------------------------------------ */
/* Inline                                                              */
/* ------------------------------------------------------------------ */

const INLINE = [
  { type: "strong", re: /\*\*(?=\S)(.*?\S)\*\*/ },
  { type: "em", re: /\*(?=[^\s*])([^*]*?[^\s*])\*/ },
  { type: "link", re: /\[([^[\]\n]+)\]\(([^\s()]+)\)/ },
];

/**
 * @param {string} text - One line
 * @param {boolean} allowLinks - false inside link text (no nested links)
 * @returns {Array<string|React.ReactElement>}
 */
function renderInline(text, allowLinks) {
  const nodes = [];
  let rest = text;
  while (rest) {
    let best = null;
    for (const rule of INLINE) {
      if (rule.type === "link" && !allowLinks) continue;
      const m = rule.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { rule, m };
    }
    if (!best) {
      nodes.push(rest);
      break;
    }
    const { rule, m } = best;
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    nodes.push(renderMatch(rule.type, m, allowLinks, nodes.length));
    rest = rest.slice(m.index + m[0].length);
  }
  return nodes;
}

function renderMatch(type, m, allowLinks, key) {
  if (type === "strong") return <strong key={key}>{renderInline(m[1], allowLinks)}</strong>;
  if (type === "em") return <em key={key}>{renderInline(m[1], allowLinks)}</em>;

  const [source, label, target] = m;
  const children = renderInline(label, false);
  switch (linkKind(target)) {
    case "internal":
      return <Link key={key} to={target}>{children}</Link>;
    case "external":
      return (
        <a key={key} href={target} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    case "mailto":
      return <a key={key} href={target}>{children}</a>;
    default:
      return source; // not an allowed target: literal text
  }
}
